<template>
  <div class="sad-page">
    <div class="sad-header">
      <h1 class="sad-title">儀表板</h1>
      <p class="sad-date">{{ todayLabel }}</p>
    </div>

    <div v-if="storeAdminStore.statsLoading" class="sad-loading">
      <span class="sad-spinner" />
      <p>載入統計資料…</p>
    </div>

    <div v-else-if="storeAdminStore.statsError" class="sad-error">
      <p>{{ storeAdminStore.statsError }}</p>
      <button class="sad-btn sad-btn--secondary" @click="load">重新載入</button>
    </div>

    <template v-else>
      <!-- 統計卡片 -->
      <div class="sad-stats">
        <router-link
          :to="{ name: 'StoreAdminReservations', query: { status: 'pending' } }"
          class="sad-stat-card sad-stat-card--warn sad-stat-card--link"
        >
          <p class="sad-stat-card__label">待確認</p>
          <p class="sad-stat-card__value">{{ storeAdminStore.pendingCount }}</p>
          <p class="sad-stat-card__hint">點擊查看 →</p>
        </router-link>
        <router-link
          :to="{ name: 'StoreAdminReservations', query: { date: todayDate } }"
          class="sad-stat-card sad-stat-card--link"
        >
          <p class="sad-stat-card__label">今日預約</p>
          <p class="sad-stat-card__value">{{ storeAdminStore.todayCount }}</p>
          <p class="sad-stat-card__hint">點擊查看 →</p>
        </router-link>
        <router-link
          :to="{ name: 'StoreAdminReservations' }"
          class="sad-stat-card sad-stat-card--info sad-stat-card--link"
        >
          <p class="sad-stat-card__label">本週預約</p>
          <p class="sad-stat-card__value">{{ storeAdminStore.weekCount }}</p>
          <p class="sad-stat-card__hint">點擊查看 →</p>
        </router-link>
        <router-link
          :to="{ name: 'StoreAdminReservations' }"
          class="sad-stat-card sad-stat-card--success sad-stat-card--link"
        >
          <p class="sad-stat-card__label">本月預約</p>
          <p class="sad-stat-card__value">{{ storeAdminStore.monthCount }}</p>
          <p class="sad-stat-card__hint">點擊查看 →</p>
        </router-link>
      </div>

      <!-- 待確認清單 -->
      <section v-if="storeAdminStore.pendingReservations.length > 0" class="sad-section">
        <div class="sad-section__head">
          <h2 class="sad-section__title">
            待確認預約
            <span class="sad-badge sad-badge--warn">{{ storeAdminStore.pendingCount }}</span>
          </h2>
          <router-link
            :to="{ name: 'StoreAdminReservations', query: { status: 'pending' } }"
            class="sad-link"
          >查看全部 →</router-link>
        </div>

        <div class="sad-pending-list">
          <div
            v-for="r in storeAdminStore.pendingReservations"
            :key="r.id"
            class="sad-pending-item"
          >
            <div class="sad-pending-item__info">
              <span class="sad-pending-item__date">{{ r.slot_date }} {{ fmtTime(r.start_time) }}</span>
              <span class="sad-pending-item__name">{{ r.customer_name }}</span>
              <span class="sad-pending-item__service">{{ r.service_item_name || r.service_name }}</span>
            </div>
            <div class="sad-pending-item__actions">
              <button
                class="sad-btn sad-btn--confirm sad-btn--sm"
                :disabled="actionLoading[r.id]"
                @click="updateStatus(r, 'confirmed')"
              >確認</button>
              <button
                class="sad-btn sad-btn--cancel sad-btn--sm"
                :disabled="actionLoading[r.id]"
                @click="updateStatus(r, 'cancelled')"
              >取消</button>
            </div>
            <p v-if="actionErrors[r.id]" class="sad-action-error">{{ actionErrors[r.id] }}</p>
          </div>
        </div>
      </section>

      <div v-else class="sad-no-pending">
        <span>✓</span> 目前沒有待確認的預約
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStoreAdminStore } from '@/store/storeAdminStore'

const storeAdminStore = useStoreAdminStore()

const todayLabel = new Date().toLocaleDateString('zh-TW', {
  timeZone: 'Asia/Taipei',
  year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
})
const todayDate = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' })

const actionLoading = ref({})
const actionErrors  = ref({})

const fmtTime = (t) => t ? t.slice(0, 5) : ''

async function updateStatus(reservation, newStatus) {
  const id = reservation.id
  actionLoading.value[id] = true
  actionErrors.value[id]  = ''
  const result = await storeAdminStore.updateReservationStatus(id, newStatus)
  if (result.success) {
    storeAdminStore.pendingReservations = storeAdminStore.pendingReservations.filter(r => r.id !== id)
    storeAdminStore.pendingCount = Math.max(0, storeAdminStore.pendingCount - 1)
    if (newStatus === 'confirmed' || newStatus === 'cancelled') {
      // also update today count if needed (skip for simplicity)
    }
  } else {
    actionErrors.value[id] = result.message || '操作失敗'
  }
  actionLoading.value[id] = false
}

async function load() {
  await storeAdminStore.fetchDashboardStats()
}

onMounted(() => { load() })
</script>

<style scoped>
.sad-page { max-width: 960px; }

.sad-announcement {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.sad-section--ann { margin-bottom: 20px; }
.sad-ann {
  background: #fff8e1; border: 1px solid #ffe082; border-left: 4px solid #f59e0b;
  border-radius: 8px; padding: 10px 14px; margin-bottom: 8px;
}
.sad-ann__summary {
  cursor: pointer; list-style: none; font-weight: 600; color: #333; font-size: 0.92rem;
}
.sad-ann__summary::-webkit-details-marker { display: none; }
.sad-ann__summary::after { content: ' ▼'; font-size: 0.7rem; color: #aaa; }
details[open] .sad-ann__summary::after { content: ' ▲'; }
.sad-ann__content { font-size: 0.88rem; color: #555; margin: 8px 0 0; white-space: pre-wrap; line-height: 1.7; }

.sad-announcement__summary {
  display: flex; gap: 10px; align-items: center; cursor: pointer; list-style: none;
}
.sad-announcement__summary::-webkit-details-marker { display: none; }
.sad-announcement__summary::after { content: ' ▼'; font-size: 0.7rem; color: #aaa; margin-left: auto; }
details[open] .sad-announcement__summary::after { content: ' ▲'; }
.sad-announcement__icon { font-size: 1.2rem; flex-shrink: 0; }
.sad-announcement__title { font-weight: 700; color: #333; margin: 0; }
.sad-announcement__content { font-size: 0.88rem; color: #555; margin: 10px 0 0; white-space: pre-wrap; line-height: 1.6; padding-left: 4px; }

.sad-guide { margin-bottom: 16px; }
.sad-guide__toggle {
  width: 100%; text-align: left; background: #f5f0ec;
  border: 1px solid #e0d5cc; border-radius: 8px; padding: 12px 16px;
  font-size: 0.95rem; font-weight: 600; color: #5a3e2b; cursor: pointer;
  list-style: none;
}
.sad-guide__toggle::-webkit-details-marker { display: none; }
.sad-guide__toggle::after { content: ' ▼'; font-size: 0.75rem; }
details[open] .sad-guide__toggle::after { content: ' ▲'; }
.sad-guide__content {
  background: #faf7f4; border: 1px solid #e0d5cc; border-top: none;
  border-radius: 0 0 8px 8px; padding: 16px 18px; font-size: 0.88rem;
  color: #555; line-height: 1.8;
}
.sad-guide__content p { margin: 0 0 8px; }
.sad-guide__section { font-weight: 700; color: #333; margin-top: 12px !important; }
.sad-guide__ann { background: #fff8e1; border-left: 3px solid #f59e0b; border-radius: 6px; padding: 10px 14px; margin-bottom: 10px; }
.sad-guide__ann-title { font-weight: 700; color: #333; margin: 0 0 6px; font-size: 0.92rem; }
.sad-guide__ann-content { font-size: 0.85rem; color: #555; margin: 0; white-space: pre-wrap; line-height: 1.7; }
.sad-guide__divider { border: none; border-top: 1px solid #e0d5cc; margin: 12px 0 16px; }

.sad-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 8px;
}
.sad-title { font-size: 1.6rem; color: #2c3e50; margin: 0; }
.sad-date  { font-size: 0.88rem; color: #95a5a6; margin: 0; }

.sad-loading {
  display: flex; flex-direction: column; align-items: center;
  gap: 12px; padding: 60px; color: #aaa;
}
.sad-error { text-align: center; padding: 48px; color: #c0392b; }
.sad-spinner {
  display: inline-block; width: 28px; height: 28px;
  border: 3px solid #ddd; border-top-color: #8b6f5c;
  border-radius: 50%; animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* 統計卡片 */
.sad-stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 36px;
}
.sad-stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px 20px 18px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  border-left: 4px solid #dce1e7;
  text-decoration: none;
  display: block;
}
.sad-stat-card--link {
  transition: box-shadow 0.18s, transform 0.18s;
  cursor: pointer;
}
.sad-stat-card--link:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  transform: translateY(-2px);
}
.sad-stat-card--warn    { border-left-color: #f39c12; }
.sad-stat-card--info    { border-left-color: #3498db; }
.sad-stat-card--success { border-left-color: #27ae60; }
.sad-stat-card__label {
  font-size: 0.78rem; color: #7f8c8d;
  margin: 0 0 8px; text-transform: uppercase; letter-spacing: 0.04em;
}
.sad-stat-card__value {
  font-size: 2.4rem; font-weight: 700; color: #2c3e50;
  margin: 0 0 6px; line-height: 1;
}
.sad-stat-card__hint {
  font-size: 0.75rem; color: #bdc3c7; margin: 0;
}

/* 待確認清單 */
.sad-section { margin-bottom: 32px; }
.sad-section__head {
  display: flex; justify-content: space-between;
  align-items: center; margin-bottom: 14px; gap: 12px;
}
.sad-section__title {
  font-size: 1rem; color: #2c3e50; margin: 0;
  display: flex; align-items: center; gap: 8px;
}
.sad-link {
  font-size: 0.85rem; color: #8b6f5c;
  text-decoration: none; white-space: nowrap;
}
.sad-link:hover { text-decoration: underline; }

.sad-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; padding: 2px 7px; border-radius: 20px;
  font-size: 0.78rem; font-weight: 700;
}
.sad-badge--warn { background: #fef3cd; color: #856404; }

.sad-pending-list {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #ede7e2;
  overflow: hidden;
}
.sad-pending-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f3f3;
  flex-wrap: wrap;
}
.sad-pending-item:last-child { border-bottom: none; }
.sad-pending-item__info {
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
}
.sad-pending-item__date {
  font-size: 0.82rem; color: #8b6f5c;
  white-space: nowrap;
}
.sad-pending-item__name {
  font-weight: 600; color: #2c3e50; font-size: 0.9rem;
}
.sad-pending-item__service {
  font-size: 0.82rem; color: #7f8c8d;
}
.sad-pending-item__actions {
  display: flex; gap: 8px; flex-shrink: 0;
}

.sad-no-pending {
  text-align: center; padding: 32px; color: #27ae60;
  background: #f0faf4; border-radius: 10px;
  border: 1px solid #c3e6cb; font-size: 0.92rem;
}

/* 按鈕 */
.sad-btn {
  display: inline-flex; align-items: center; justify-content: center;
  border: none; border-radius: 5px; cursor: pointer;
  font-size: 0.82rem; padding: 6px 12px;
  transition: opacity 0.15s; white-space: nowrap;
}
.sad-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sad-btn--sm      { font-size: 0.8rem; padding: 5px 10px; }
.sad-btn--confirm  { background: #27ae60; color: #fff; }
.sad-btn--confirm:hover:not(:disabled)  { background: #219a52; }
.sad-btn--cancel   { background: transparent; border: 1px solid #e74c3c; color: #e74c3c; }
.sad-btn--cancel:hover:not(:disabled)   { background: #fdedec; }
.sad-btn--secondary {
  background: transparent; border: 1px solid #bdc3c7; color: #7f8c8d;
  font-size: 0.9rem; padding: 8px 18px; border-radius: 7px;
}
.sad-btn--secondary:hover { background: #f4f6f9; }

.sad-action-error {
  font-size: 0.78rem; color: #e74c3c;
  margin: 4px 0 0; width: 100%;
}

@media (max-width: 600px) {
  .sad-stats { grid-template-columns: repeat(2, 1fr); }
  .sad-stat-card__value { font-size: 1.8rem; }
}
</style>
